export interface AuthUser {
  id: number;
  firstName: string;
  lastName: string;
  name: string;
  email: string;
  userId?: string;
  clientIds: number[];
  phone?: string;
  twoFactorAuth: boolean;
}

export interface ClientSettings {
  id: number;
  fiO2RedThreshold?: number;
  fiO2YellowThreshold?: number;
  peepRedThreshold?: number;
  peepYellowThreshold?: number;
  infusionHistoryInHours: number;
}

export enum PushNotificationProvider {
  None = 0,
  Firebase = 1,
  Apple = 2
}

export interface PushNotificationInfo {
  provider: PushNotificationProvider;
  deviceToken: string;
}

export type BaseSuccessLoginResult = {
  isSuccess: true;
  jwt: string;
  isPasswordExpiringSoon: boolean;
  daysToPasswordExpiration: number;
};

export type LoginResult =
  | (BaseSuccessLoginResult & { hasBiometrics: boolean })
  | { isSuccess: false; errorMessage: string };

export type BiometricLoginResult = BaseSuccessLoginResult | { isSuccess: false; isAccountLocked: boolean };

export type VerifyCodeResponse =
  | BaseSuccessLoginResult
  | { isSuccess: false; isAccountLocked: boolean; errorMessage?: string };

export type UpdatePhoneOtpResponse = { isSuccess: boolean };

export interface DeviceInfo {
  guid?: string;
  type?: string;
  os?: string;
  make?: string;
  model?: string;
}

export interface PasswordRules {
  minLength: number;
  maxLength: number;
  passwordHistory: number;
}

export interface UpdateUserPhoneRequest {
  userid: string;
  phonenumber: string | number | null | undefined;
  IsDeletedRequired: boolean;
}

export interface IdnAndClients {
  id: number;
  name: string;
  clients: Client[];
}

export interface Client {
  id: number;
  name: string;
}

export interface PatientLocation {
  careArea?: string;
  room?: string;
  bed?: string;
  display?: string;
}

export interface PatientListViewModel {
  id: number;
  firstName: string;
  lastName: string;
  location: PatientLocation;
  watchers: Watcher[];
  isSubscribed: boolean;
}

export interface MyPatientsList {
  timeLastClientVentLoadUtc?: string;
  patients: MyPatientsListItem[];
}

export interface MyPatientsListItem {
  id: number;
  firstName: string;
  lastName: string;
  ventStatus: PatientStatus;
  infusionStatus: PatientStatus;
  location: PatientLocation;
  ventModeLabel?: string;
  ventMode: VentMode;
  isNonInvasive?: boolean;
  fiO2?: number;
  peep?: number;
  daysOnVent: number;
  activeNotificationCount: number;
  vaeSurveillanceType?: VaeSurveillanceType;
}

export interface PatientDetailsViewModel {
  id: number;

  /** Indicates that the customer has the RKP infusion feature enabled */
  isClientInfusionEnabled?: boolean;

  firstName: string;
  lastName: string;
  ventStatus: PatientStatus;
  infusionStatus: PatientStatus;
  gender: Gender;
  dateOfBirth?: string;
  daysOnVent: number;
  timeLastVentMessageUtc?: string;
  timeLastClientVentLoadUtc?: string;
  timeLastClientInfusionLoadUtc?: string;
  location: PatientLocation;
  ventMonitor: VentMonitorModel;
  ventSetting: VentSettingModel;
  infusionHistory?: Infusion[];
  watchers: Watcher[];
  notifications: Notification[];
  vaeSurveillanceType?: VaeSurveillanceType;
}

export interface Infusion {
  id: number;
  timeStartedUtc: string;
  timeStartedLocal: string;
  timeEndedUtc?: string;
  timeEndedLocal?: string;
  drugName: string;
  drugDisplayOrder: number;
  doseAmount: number;
  doseUnits: string;
  isBolus: boolean;
  guardrailStatus: GuardrailStatus;
}

export interface VentMonitorModel {
  cdyn?: number;
  cdynUnits?: string;

  etCO2?: number;
  etCO2Units?: string;

  fiO2?: number;
  fiO2Units?: string;

  mandatoryRate?: number;
  mandatoryRateUnits?: string;

  mandatoryVte?: number;
  mandatoryVteUnits?: string;

  minuteVentilation?: number;
  minuteVentilationUnits?: string;

  peep?: number;
  peepUnits?: string;

  pefr?: number;
  pefrUnits?: string;

  pifr?: number;
  pifrUnits?: string;

  pMean?: number;
  pMeanUnits?: string;

  pPeak?: number;
  pPeakUnits?: string;

  rsbi?: number;
  rsbiUnits?: string;

  spontaneousRate?: number;
  spontaneousRateUnits?: string;

  spontaneousVe?: number;
  spontaneousVeUnits?: string;

  spontaneousTidalVolume?: number;
  spontaneousTidalVolumeUnits?: string;

  timeExpiration?: number;
  timeExpirationUnits?: string;

  timeInspiration?: number;
  timeInspirationUnits?: string;

  totalRate?: number;
  totalRateUnits?: string;

  vte?: number;
  vteUnits?: string;

  vti?: number;
  vtiUnits?: string;

  workOfBreathingMeasured?: number;
  workOfBreathingMeasuredUnits?: string;

  autoPeep?: number;
  autoPeepUnits?: string;

  cstat?: number;
  cstatUnits?: string;

  p100?: number;
  p100Units?: string;

  pplat?: number;
  pplatUnits?: string;

  spO2?: number;
  spO2Units?: string;

  ie?: string;

  mip?: number;
  mipUnits?: string;

  flow?: number;
  flowUnits?: string;

  leakFlow?: number;
  leakFlowUnits?: string;

  mvInsp?: number;
  mvInspUnits?: string;

  percentSpon?: number;
  percentSponUnits?: string;

  leak?: number;
  leakUnits?: string;

  woBi?: number;
  woBiUnits?: string;
}

export interface VentSettingModel {
  ventTypeKey?: VentType;
  ventTypeCode?: string;

  ventSerialNumber?: string;
  ventName?: string;

  mode?: VentMode;
  modeLabel?: string;

  isNonInvasive?: boolean;

  apneaInterval?: number;
  apneaIntervalUnits?: string;

  fiO2?: number;
  fiO2Units?: string;

  fiO2High?: number;
  fiO2HighUnits?: string;

  fiO2Low?: number;
  fiO2LowUnits?: string;

  flowPeak?: number;
  flowPeakUnits?: string;

  flowTrigger?: number;
  flowTriggerUnits?: string;

  inspirationPause?: number;
  inspirationPauseUnits?: string;

  inspirationPressure?: number;
  inspirationPressureUnits?: string;

  inspirationTime?: number;
  inspirationTimeUnits?: string;

  nCpap?: number;
  nCpapUnits?: string;

  peep?: number;
  peepUnits?: string;

  peepLow?: number;
  peepLowUnits?: string;

  pPeakHigh?: number;
  pPeakHighUnits?: string;

  pPeakLow?: number;
  pPeakLowUnits?: string;

  pressureHigh?: number;
  pressureHighUnits?: string;

  pressureLow?: number;
  pressureLowUnits?: string;

  psv?: number;
  psvUnits?: string;

  rate?: number;
  rateUnits?: string;

  rateHigh?: number;
  rateHighUnits?: string;

  timeHigh?: number;
  timeHighUnits?: string;

  timeLow?: number;
  timeLowUnits?: string;

  veHigh?: number;
  veHighUnits?: string;

  veLow?: number;
  veLowUnits?: string;

  volume?: number;
  volumeUnits?: string;

  vteAlarmLow?: number;
  vteAlarmLowUnits?: string;

  vteHigh?: number;
  vteHighUnits?: string;

  vteLow?: number;
  vteLowUnits?: string;

  psvTmax?: number;
  psvTmaxUnits?: string;

  waveform?: string;

  minVol?: number;
  minVolUnits?: string;

  height?: number;
  heightUnits?: string;

  flow?: number;
  flowUnits?: string;

  riseMode?: string;

  riseValue?: number;
  riseValueUnits?: string;

  expTrigMode?: string;

  expTrigValue?: number;
  expTrigValueUnits?: string;

  etCO2Low?: number;
  etCO2LowUnits?: string;

  etCO2High?: number;
  etCO2HighUnits?: string;

  rateLow?: number;
  rateLowUnits?: string;

  spO2Low?: number;
  spO2LowUnits?: string;

  spO2High?: number;
  spO2HighUnits?: string;
}

export interface RequestVerificationPayload {
  channel: VerificationMethod;
  deviceGuid: string;
}

export interface TrustedDevice {
  id: number;
  guid: string;
  type: string;
  os: string;
  make: string;
  model: string;
  createdDateTime: string;
  lastLoginDateTime: string;
}

export type TrustedDeviceSecret = string;
export type BiometricSecret = string;

export interface UpdateUserResponse {
  errorMessage?: string;
  isSuccess: boolean;
  successMessage?: string;
}

export enum VentType {
  Unknown = 0,
  Avea = 1,
  Enve = 2,
  Vela = 3,
  LTV = 4,
  Bellavista = 5
}

export enum VentMode {
  Unknown = 0,
  Standby = 1,
  VolumeAc = 2,
  VolumeSimv = 3,
  PressureAc = 4,
  PressureSimv = 5,
  PrvcAc = 6,
  PrvcSimv = 7,
  TcplAc = 8,
  TcplSimv = 9,
  AprvBiPhasic = 10,
  CpapPsv = 11,
  NasalCpap = 12,
  NasalCpapImv = 13
}

export enum Gender {
  Female = 1,
  Male = 2
}

export enum PatientStatus {
  Active = 0,
  Hidden = 1,
  Stale = 2
}

export enum GuardrailStatus {
  Normal = 0,
  Below = 1,
  Above = 2
}

export enum NotificationType {
  Marker = 1,
  SettingChanged = 2
}

export interface Notification {
  id: number;
  dateStartedLocal: string;
  message: string;
  isActive: boolean;
  notificationType: NotificationType;
}

export interface Watcher {
  id: number;
  firstName: string;
  lastName: string;
}

export enum VerificationMethod {
  Mobile = "mobile",
  Email = "email"
}

export enum VaeSurveillanceType {
  None = 0,
  VaeDay1 = 1,
  VaeDay2 = 2,
  CriteriaMet = 3
}

export interface UserProfile {
  phoneNumber?: string;
}
